import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { Bell, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ar, fr } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

import { getNotifications, deleteNotification, deleteAllNotifications } from '@/api/notifications'
import { useNotificationRedirect } from '@/hooks/use-notification-redirect'

export function NotificationPopover() {
    const { t, i18n } = useTranslation()
    const [open, setOpen] = useState(false)
    const queryClient = useQueryClient()

    const { data, isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => getNotifications({ ordering: '-created_at' }),
        refetchInterval: 30000,
    })

    const deleteMutation = useMutation({
        mutationFn: deleteNotification,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    })

    const deleteAllMutation = useMutation({
        mutationFn: deleteAllNotifications,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    })

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation()
        deleteMutation.mutate(id)
    }

    const handleDeleteAll = () => {
        if (confirm(t('confirm_delete_all_notifications', { defaultValue: 'هل أنت متأكد من حذف جميع الإشعارات؟' }))) {
            deleteAllMutation.mutate()
        }
    }

    const { handleNotificationClick } = useNotificationRedirect()

    const notifications = (data?.results || []).filter(
        (n) => n.notification_type !== 'ANNOUNCEMENT'
    )
    const unreadCount = notifications.filter((n) => !n.is_read).length

    const getDateLocale = () => {
        return i18n.language === 'fr' ? fr : ar
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        "relative text-muted-foreground hover:text-foreground",
                        "hover:bg-muted rounded-full transition-all duration-200",
                        "hover:scale-110"
                    )}
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className={cn(
                            "absolute top-1 end-1 w-2.5 h-2.5 bg-red-500 rounded-full",
                            "border-2 border-card animate-pulse"
                        )} />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    "w-96 p-0 shadow-xl border-border/50",
                    "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
                    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
                )}
                align="end"
                sideOffset={8}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border/50 p-4 bg-muted/30">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                        <Bell className="h-4 w-4 text-primary" />
                        {t('notifications', { defaultValue: 'الإشعارات' })}
                    </h4>
                    <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                            <span className="px-2.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                        {notifications.length > 0 && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full"
                                onClick={handleDeleteAll}
                                title={t('delete_all', { defaultValue: 'حذف الكل' })}
                                disabled={deleteAllMutation.isPending}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <ScrollArea className="h-[400px]">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-muted-foreground">
                            <div className="relative">
                                <Bell className="h-10 w-10 opacity-20 animate-pulse" />
                                <div className="absolute inset-0 animate-ping">
                                    <Bell className="h-10 w-10 opacity-10" />
                                </div>
                            </div>
                            <p className="text-sm font-medium">{t('loading', { defaultValue: 'جاري التحميل...' })}</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-muted-foreground">
                            <div className="rounded-full bg-muted/50 p-4">
                                <Bell className="h-10 w-10 opacity-30" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium">{t('no_notifications', { defaultValue: 'لا توجد إشعارات' })}</p>
                                <p className="text-xs mt-1">{t('all_caught_up', { defaultValue: 'أنت على اطلاع بكل شيء' })}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-border/50">
                            {notifications.map((notification, index) => (
                                <div
                                    key={notification.id}
                                    className={cn(
                                        "flex flex-col gap-2 p-4 transition-all duration-200",
                                        "hover:bg-muted/50 cursor-pointer relative group",
                                        "hover:shadow-sm",
                                        !notification.is_read
                                            ? "bg-primary/5 dark:bg-primary/10 border-s-2 border-primary"
                                            : "bg-card hover:bg-muted/30",
                                        "animate-in fade-in-0 slide-in-from-top-1"
                                    )}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                    onClick={(e) => handleNotificationClick(e, notification, setOpen)}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <span className={cn(
                                            "text-sm leading-tight flex-1",
                                            !notification.is_read
                                                ? "font-bold text-foreground"
                                                : "font-medium text-foreground/90"
                                        )}>
                                            {notification.title}
                                        </span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {!notification.is_read && (
                                                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full"
                                                onClick={(e) => handleDelete(e, notification.id)}
                                                disabled={deleteMutation.isPending}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                    <p className={cn(
                                        "text-sm leading-relaxed whitespace-pre-line",
                                        !notification.is_read
                                            ? "text-foreground/80"
                                            : "text-muted-foreground"
                                    )}>
                                        {notification.message}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">
                                            {formatDistanceToNow(new Date(notification.created_at * 1000), { addSuffix: true, locale: getDateLocale() })}
                                        </span>
                                        {!notification.is_read && (
                                            <span className="text-[10px] text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                                                {t('click_to_mark_read', { defaultValue: 'انقر لتعليمه كمقروء' })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    )
}
